import { useCallback } from "react";
import type { BackendPermission, Me } from "@/api/types";
import { useStoreContext } from "@/hooks/use-store-context";

/**
 * UX-level capabilities. They only decide what the UI offers; the backend enforces every
 * permission again on each request. A hidden button is never a security control.
 */
export const CAPABILITIES = {
  "opportunity:read": "opportunity:read",
  "opportunity:update": "opportunity:write",
  "opportunity:assign": "opportunity:assign",
  "vehicle:update": "opportunity:write",
  "conversation:read": "conversation:read",
  "conversation:message": "message:send",
  "conversation:takeover": "message:send",
  "ai:retry": "ai:rerun",
  "valuation:write": "valuation:write",
  "valuation:approve": "valuation:approve",
  "offer:create": "offer:create",
  "offer:approve": "offer:approve",
  "offer:send": "offer:send",
  "appointment:update": "appointment:write",
  "purchase:record": "purchase:write",
  "analytics:view": "opportunity:read",
  "settings:manage": "store:manage",
  "members:manage": "members:manage",
} as const satisfies Record<string, BackendPermission>;

export type Capability = keyof typeof CAPABILITIES;

/**
 * `storeId` given → permission in that store. Otherwise → in the active store, or (in the
 * "All stores" view) in at least one accessible store.
 */
export function can(me: Me, capability: Capability, storeId?: string | null, activeStoreId?: string | null): boolean {
  const permission = CAPABILITIES[capability];
  const target = storeId ?? activeStoreId ?? null;
  if (target) {
    return me.accessibleStores.find((s) => s.storeId === target)?.permissions.includes(permission) ?? false;
  }
  return me.accessibleStores.some((s) => s.permissions.includes(permission));
}

export function usePermissions() {
  const { me, activeStore } = useStoreContext();
  const check = useCallback((capability: Capability, storeId?: string | null) => can(me, capability, storeId, activeStore?.storeId), [me, activeStore]);
  return { can: check };
}
